import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, Headset, UserCheck } from "lucide-react";
import { api } from "../lib/api";

const SUGGESTIONS = [
  "Comment pointer mon entrée ?",
  "Comment approuver une feuille de temps ?",
  "Comment ajouter un employé ?",
  "C'est quoi le mode hors ligne ?",
];

const WELCOME = { id: 0, sender: "ai", content: "Bonjour 👋 Je suis l'assistant PROGÈRE. Posez-moi vos questions sur le fonctionnement de l'application !" };

function StatusBanner({ status, tech }) {
  if (status === "waiting") return <div className="chat-status chat-status-wait" data-testid="chat-status-waiting"><Loader2 size={13} className="spin" /> En attente d'un technicien…</div>;
  if (status === "live") return <div className="chat-status chat-status-live" data-testid="chat-status-live"><UserCheck size={13} /> {tech || "Technicien"} est en ligne</div>;
  if (status === "closed") return <div className="chat-status chat-status-closed" data-testid="chat-status-closed">Conversation fermée</div>;
  return null;
}

function Bubble({ m }) {
  if (m.sender === "system") return <div className="chat-system" data-testid="ai-msg-system">{m.content}</div>;
  const cls = m.sender === "user" ? "user" : m.sender === "tech" ? "tech" : "assistant";
  return (
    <div className={`ai-msg ${cls}`} data-testid={`ai-msg-${m.sender}`}>
      {m.sender === "tech" && <div className="chat-sender-label"><Headset size={11} /> {m.sender_name || "Technicien"}</div>}
      {m.content}
    </div>
  );
}

export function AiHelp({ authed = false }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null); // live-chat session (authed)
  const sid = useRef(null);            // assistant session id (public)
  const idc = useRef(1);
  const scrollRef = useRef(null);
  const lastTyping = useRef(0);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs, open]);

  // Dedicated entry: open from anywhere via window event
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("pp:open-livechat", h);
    return () => window.removeEventListener("pp:open-livechat", h);
  }, []);

  // Authenticated live-chat: load session on first open
  useEffect(() => {
    if (!open || !authed || session) return;
    api.chatStart().then((d) => { setSession(d.session); setMsgs(d.messages); }).catch(() => {});
  }, [open, authed, session]);

  // Polling (authed, open, not closed)
  const poll = useCallback(() => {
    if (!authed || !session || session.status === "closed") return;
    api.chatPoll(session.id).then((d) => { setSession(d.session); setMsgs(d.messages); }).catch(() => {});
  }, [authed, session]);
  useEffect(() => {
    if (!open || !authed || !session || session.status === "closed") return;
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [open, authed, session, poll]);

  const onType = (e) => {
    setInput(e.target.value);
    if (!authed || !session || session.status === "closed") return;
    const now = Date.now();
    if (now - lastTyping.current > 2500) { lastTyping.current = now; api.chatTyping(session.id).catch(() => {}); }
  };

  const sendPublic = async (q) => {
    setMsgs((m) => [...m, { id: idc.current++, sender: "user", content: q }]);
    setBusy(true);
    try {
      const r = await api.assistantChat({ message: q, session_id: sid.current });
      sid.current = r.session_id;
      setMsgs((m) => [...m, { id: idc.current++, sender: "ai", content: r.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { id: idc.current++, sender: "ai", content: "Désolé, je suis momentanément indisponible. Réessayez ou contactez le support." }]);
    } finally { setBusy(false); }
  };

  const sendLive = async (q) => {
    setMsgs((m) => [...m, { id: `tmp-${Date.now()}`, sender: "user", content: q }]);
    setBusy(true);
    try {
      const d = await api.chatMessage(session.id, q);
      setSession(d.session); setMsgs(d.messages);
    } catch (e) {
      poll();
    } finally { setBusy(false); }
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    if (authed && session) return sendLive(q);
    return sendPublic(q);
  };

  const closed = authed && session && session.status === "closed";
  const subtitle = authed
    ? (session?.status === "live" ? "Support en direct avec un technicien"
      : session?.status === "waiting" ? "Mise en relation avec un technicien"
      : "Aide IA · escalade vers un technicien")
    : "Aide & fonctionnement de l'app";

  return (
    <>
      <button className="ai-fab" onClick={() => setOpen((o) => !o)} aria-label="Aide IA" data-testid="ai-help-fab">
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div className="ai-panel fade-in" data-testid="ai-help-panel">
          <div className="ai-head">
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span className="ai-orb">{authed ? <Headset size={16} /> : <Sparkles size={16} />}</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{authed ? "Support PROGÈRE" : "Assistant PROGÈRE"}</div>
                <div style={{ fontSize: 11, color: "var(--muted)" }}>{subtitle}</div>
              </div>
            </div>
            <button className="top-btn" onClick={() => setOpen(false)} data-testid="ai-help-close"><X size={16} /></button>
          </div>

          {authed && session && <StatusBanner status={session.status} tech={session.assigned_tech_name} />}

          <div className="ai-body" ref={scrollRef} data-testid="ai-messages">
            {msgs.map((m) => <Bubble key={m.id} m={m} />)}
            {busy && <div className="ai-msg assistant"><Loader2 size={15} className="spin" /></div>}
            {authed && session?.tech_typing && (
              <div className="typing-dots" data-testid="client-typing-indicator"><span></span><span></span><span></span> {session.assigned_tech_name || "Le technicien"} est en train d'écrire…</div>
            )}
            {!authed && msgs.length <= 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {SUGGESTIONS.map((s) => (
                  <button key={s} className="ai-chip" onClick={() => send(s)} data-testid="ai-suggestion">{s}</button>
                ))}
              </div>
            )}
          </div>

          {closed ? (
            <div className="ai-input" style={{ justifyContent: "center" }}>
              <button className="btn btn-outline btn-sm" onClick={() => { setSession(null); setMsgs([WELCOME]); }} data-testid="chat-new">Nouvelle conversation</button>
            </div>
          ) : (
            <form className="ai-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
              <input value={input} maxLength={1500} onChange={onType} placeholder="Écrivez votre message…" data-testid="ai-input" />
              <button type="submit" className="btn btn-gold btn-sm" disabled={busy || !input.trim()} data-testid="ai-send"><Send size={15} /></button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
